import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useEffect } from 'react';
import { Card } from 'react-bootstrap';
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);
const CriterionRatingCard = ({ criterion, weeks, ratingMode }) => {
    const displayRating = ratingMode === 'latest' ? criterion.latestRating : criterion.currentRating;
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);
    useEffect(() => {
        if (!chartRef.current)
            return;
        // Destroy existing chart if it exists
        if (chartInstanceRef.current) {
            chartInstanceRef.current.destroy();
        }
        // Prepare data for chart
        const labels = [];
        const data = [];
        // Fill in all weeks, using null for missing data
        for (let week = 1; week <= weeks; week++) {
            labels.push(`Wk ${week}`);
            const weekData = criterion.historicalRatings.find(r => r.week === week);
            data.push(weekData ? weekData.rating : NaN);
        }
        // Create new chart
        const ctx = chartRef.current.getContext('2d');
        if (!ctx)
            return;
        chartInstanceRef.current = new Chart(ctx, {
            type: 'line',
            data: {
                labels,
                datasets: [{
                        label: 'Rating',
                        data,
                        borderColor: '#1d4ed8', // blue-700
                        backgroundColor: 'rgba(29, 78, 216, 0.1)',
                        borderWidth: 2,
                        pointRadius: 4,
                        pointBackgroundColor: '#2563eb', // blue-600
                        tension: 0.3,
                        fill: true,
                        spanGaps: false
                    }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        mode: 'index',
                        intersect: false
                    }
                },
                scales: {
                    x: {
                        grid: { display: false },
                        border: { display: true, color: '#e5e7eb' },
                        ticks: { font: { size: 10 } }
                    },
                    y: {
                        min: 0.5,
                        max: 5.5,
                        ticks: { stepSize: 1, display: false },
                        grid: { display: false },
                        border: { display: false }
                    }
                },
                layout: {
                    padding: { left: 4, right: 4, top: 8, bottom: 8 }
                }
            }
        });
        // Cleanup on unmount
        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
                chartInstanceRef.current = null;
            }
        };
    }, [criterion, weeks]);
    // Calculate progress bar width (0-100% based on 5.0 scale)
    const progressWidth = displayRating !== null
        ? (displayRating / 5.0) * 100
        : 0;
    return (_jsx(Card, { className: "mb-3", style: { backgroundColor: 'var(--bg-light)' }, children: _jsxs(Card.Body, { className: "p-3 p-md-4", children: [_jsxs("div", { className: "d-flex justify-content-between align-items-center mb-2", children: [_jsx("span", { className: "fw-semibold", style: { fontSize: 'var(--font-size-base)' }, children: criterion.criteriaName }), _jsx("span", { className: "fw-bold text-primary", style: { fontSize: 'var(--font-size-lg)' }, children: displayRating !== null
                                ? `${displayRating.toFixed(1)} / 5.0`
                                : 'N/A' })] }), _jsx("div", { className: "w-100 rounded mb-3", style: {
                        height: '8px',
                        backgroundColor: '#e5e7eb',
                        boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.1)'
                    }, children: _jsx("div", { className: "h-100 rounded", style: {
                            width: `${progressWidth}%`,
                            backgroundColor: '#3b82f6', // blue-500
                            transition: 'width 0.3s ease-in-out'
                        } }) }), _jsx("div", { style: { height: '140px', position: 'relative' }, children: _jsx("canvas", { ref: chartRef }) })] }) }));
};
export default CriterionRatingCard;
